import {
  BreadcrumbEntry,
  SubPageBlueprint,
} from '@backstage/frontend-plugin-api';
import { createFrontendModule } from '@backstage/frontend-plugin-api';
import { Navigate, Route, Routes, useParams } from 'react-router-dom';
import { SecretsContextProvider } from '@backstage/plugin-scaffolder-react';
import { TaskPage } from '@backstage/plugin-scaffolder';
import { CreateHub } from './CreateHub';
import { TemplateWizard } from './TemplateWizard';
import { PipelineRequestPage } from './PipelineRequestPage';
import { RequestsPage } from '../requests/RequestsPage';

/**
 * The template name as the trailing crumb, so the form reads
 * Create › Start › answerrocket-service. Mirrors what the stock sub-page
 * does with the same BreadcrumbEntry.
 */
const WizardWithBreadcrumb = () => {
  const { templateName } = useParams();
  return (
    <BreadcrumbEntry entry={{ label: templateName ?? 'Template', href: '.' }}>
      <SecretsContextProvider>
        <TemplateWizard />
      </SecretsContextProvider>
    </BreadcrumbEntry>
  );
};

const RequestWithBreadcrumb = () => {
  const { pipelineId } = useParams();
  return (
    <BreadcrumbEntry entry={{ label: pipelineId ?? 'Request', href: '.' }}>
      {/* Keyed: going from one pipeline's form straight to another reuses
          this route, and the old form's error or success screen must not
          survive into the new one. */}
      <PipelineRequestPage key={pipelineId} />
    </BreadcrumbEntry>
  );
};

/**
 * Both routes the stock sub-page carries. The list is ours; the form is the
 * scaffolder's, restated (see ./TemplateWizard). Dropping the second route is
 * what broke Choose — the sub-page matches the whole `templates/*` subtree, so
 * a wizard URL with no route here just renders the list again.
 */
const TemplatesSubPage = () => (
  <Routes>
    <Route index element={<CreateHub />} />
    {/* One segment, so it cannot collide with a template's two
        (namespace/name). A Control pipeline no template claims —
        PORTAL.md §4.1; `new-env` is the one today. Under Start, not My
        requests: filling a form in is starting something. */}
    <Route path=":pipelineId" element={<RequestWithBreadcrumb />} />
    <Route path=":namespace/:templateName" element={<WizardWithBreadcrumb />} />
  </Routes>
);

// Overrides sub-page:scaffolder/templates. The PATH stays `templates` — the
// scaffolder's own route refs (start over, a template's link) resolve to
// /create/templates/:namespace/:name — but the tab is "Start": it is the Create
// hub (./CreateHub), with the full template list beneath its featured cards.
//
// Create has exactly two tabs, Start and My requests. The stock Tasks, Actions,
// Template Editor and Templating Extensions tabs are disabled in app-config:
// the first is folded into My requests below, and the other three are authoring
// tools for template writers, who can still reach the scaffolder's API docs —
// they were the first thing a person asking for an environment saw beside it.
const templatesSubPage = SubPageBlueprint.make({
  name: 'templates',
  params: {
    path: 'templates',
    title: 'Start',
    loader: async () => <TemplatesSubPage />,
  },
});

/**
 * `/create/request` — My requests.
 *
 *   /create/request               what you asked for, and where it got to
 *   /create/request/task/:taskId  a template's run, straight after Create
 *
 * One tab for "I asked for something", whichever way it was asked. Status used
 * to live in three places — /requests (Control runs), Create › Tasks (template
 * runs) and Control itself — and the place you landed after submitting depended
 * on which form you had used. The index is the old /requests page, which now
 * redirects here.
 *
 * `task/:taskId` is the stock task view (TaskPage, i.e. OngoingTask): it needs only the `taskId` param and
 * the templates route ref, so it works under this path as well as under the
 * Tasks sub-page it was written for.
 */
/**
 * `/create/request/<pipelineId>` shipped in #76 as the request form's URL
 * before the form moved under Start; links to it (e.g. "Request one" in a
 * PlacementPicker someone left open) keep working. `task/...` is two segments,
 * so it cannot be shadowed by this one.
 */
const OldPipelineUrl = () => {
  const { pipelineId } = useParams();
  return <Navigate to={`/create/templates/${pipelineId}`} replace />;
};

const RequestSubPage = () => (
  <Routes>
    <Route index element={<RequestsPage />} />
    <Route path="task/:taskId" element={<TaskPage />} />
    <Route path=":pipelineId" element={<OldPipelineUrl />} />
  </Routes>
);

const requestSubPage = SubPageBlueprint.make({
  name: 'request',
  params: {
    path: 'request',
    title: 'My requests',
    loader: async () => <RequestSubPage />,
  },
});

export const templatesModule = createFrontendModule({
  pluginId: 'scaffolder',
  extensions: [templatesSubPage, requestSubPage],
});
